// apps/web/src/features/crm/leads/list/LeadClientPicker.tsx
//
// Debounced client search picker (#2247 FR-1/FR-4). Shared by the lead list's
// client filter and the card's bind flow so both drive the SAME
// `useClientsInfinite` read. Emits the picked client's id + name so callers can
// show a client-name chip without a second fetch. Purely a read surface —
// binding itself is confirmed in `LeadClientMatchDialog`.
import { useEffect, useMemo, useState } from 'react';
import { useClientsInfinite } from '@magimp/shared-hooks';
import { Button, Input } from '../../../../ui/index.js';
import { useTranslation } from 'react-i18next';

export interface LeadClientPickerProps {
  onPick: (client: { id: string; name: string }) => void;
}

export function LeadClientPicker({ onPick }: LeadClientPickerProps) {
  const { t } = useTranslation(['leads', 'common']);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(h);
  }, [q]);

  const listQ = useClientsInfinite(debouncedQ ? { q: debouncedQ } : {});
  const rows = useMemo(
    () => (listQ.data?.pages ?? []).flatMap((p) => p.items).slice(0, 20),
    [listQ.data],
  );

  return (
    <div
      data-testid="lead-client-picker"
      style={{
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-1)',
        padding: 8,
        background: 'var(--bg-1)',
      }}
    >
      <Input
        value={q}
        onChange={setQ}
        placeholder={t('leads:client.searchPlaceholder')}
        icon="search"
        ariaLabel={t('leads:client.searchPlaceholder')}
      />
      <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', display: 'grid', gap: 4 }}>
        {rows.map((c) => (
          <li key={c.id}>
            <Button
              kind="ghost"
              onClick={() => onPick({ id: c.id, name: c.name })}
              data-testid={`lead-client-pick-${c.id}`}
            >
              {c.name}
            </Button>
          </li>
        ))}
        {rows.length === 0 && debouncedQ && (
          <li style={{ color: 'var(--fg-2)', fontSize: 12 }}>{t('leads:client.empty')}</li>
        )}
      </ul>
    </div>
  );
}
